import React, { Component, PropTypes } from 'react'
import { connect } from 'react-redux'
import CSSModules from 'react-css-modules'
import Loader from 'react-loader'
import config from '../../config'
import container from '../../helpers/container'
import format from '../../helpers/format'
import convert from '../../helpers/convert'
import analytics from '../../helpers/analytics'
import {
  ContainerDivs,
  Chart,
  FlaggedFund,
  Link,
  LinkIcon,
  FamilyGrid,
  GetStartedInput,
  AboutBanner,
  Score,
  Grade,
  FamilyFactSheet
} from '../../components'
import * as searchActions from 'redux/modules/search'

const Styles = require('./Family.css')


/**
 * <FamilyBanner
 *   fundFamily
 * />
 * 
 * Displays a banner for the fund family that has a different tagline if the
 * family is one of the 10 families with additional data in config, or just
 * a generic fund family.
 * 
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyBanner extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundFamily: props.fundFamily || null
    }
  }

  render() {
    const { fundFamily } = this.state
    if (!fundFamily) { return null }

    return (
      <div styleName='banner-wrap'>
        <div styleName='banner'>
          <div styleName='banner-inner'>
            <div styleName='banner-taglines'>
              <div>
                <div styleName='banner-tagline'>
                  {
                    fundFamily ?
                      (
                        <span>Are your retirement funds</span>
                      ) : (
                        <span>Your retirement plan could</span>
                      )
                  }
                </div>
              </div>
              <div>
                <div styleName='banner-tagline'>
                  {
                    fundFamily ?
                      (
                        <span>invested with {fundFamily.fund_family_label}?</span>
                      ) : (
                        <span>be funding deforestation</span>
                      )
                  }
                </div>
              </div>
              <div styleName='toolkit'>
                <div styleName='toolkit-info'>
                  Check out our action toolkit and talk to your retirement
                  plan manager
                </div>
                <Link
                  to={config.routes.toolkit}
                  log={analytics.eventToToolkit()}
                  title='#Defund deforestation action toolkit'>
                  <div styleName='toolkit-action'>
                    Action toolkit<LinkIcon />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}


/**
 * <FamilySummarySentence
 *   fundFamily
 * />
 *
 * Displays sentence for the fund family that names the fund company
 * at the top of the list of funds and shareclasses for this family.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilySummarySentence extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundFamily: props.fundFamily || null
    }
  }

  render() {
    const { fundFamily } = this.state
    if (!fundFamily) { return null }

    return (
      <div styleName='summary'>
        {fundFamily.fund_family_label} funds in our database
      </div>
    )
  }
}


/**
 * <FamilyFundList
 *   fundList
 * />
 * 
 * Displays a list of funds for this family. Maps a fundList to a list of
 * FamilyFundListItem components.
 * 
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyFundList extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundList: props.fundList || null
    }
  }

  componentWillReceiveProps(props) {
    if (props.fundList) {
      this.setState({
        fundList: props.fundList
      })
    }
  }

  render() {
    const { fundList } = this.state
    if (!fundList) { return null }

    return (
      <div styleName='holding-list'>
        {
          fundList.map((fund, idx) => {
            return (
              <FamilyFundListItem
                fund={fund}
                key={'family-fund-list-item-'+idx}
              />
            )
          })
        }
      </div>
    )
  }
}


/**
 * <FamilyFundListItem 
 *   fund
 * />
 * 
 * Displays a fund in the list of funds for this family. Displays some summary
 * stats. Displays a list of tickers using the FamilyFundListItemTickerLinks
 * component.
 * 
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyFundListItem extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fund: props.fund || null
    }
  }

  render() {
    const { fund } = this.state
    if (!fund) { return null }

    const {
      fund_id,
      fund_legal_name,
      currency,
      po_all_count,
      po_all_asset,
      po_all_weight,
      grade_deforestation
    } = fund

    return (
      <div styleName='holding'>
        <div styleName='ticker-name-assets'>
          <div styleName='ticker-name'>
            <div styleName='holdings-name'>
              {fund_legal_name}
            </div>
            <FamilyFundListItemTickerLinks shareclasses={fund.shareclass} />
          </div>
          <div styleName='ticker-assets'>
            <div styleName='ticker-assets-figure'>
              {format.number(po_all_count) }
            </div>
            <div styleName='ticker-assets-label'>
              &nbsp;deforestation-risk stocks
            </div>
          </div>
          <div styleName='ticker-assets'>
            <div styleName='ticker-assets-figure'>
              {format.money(po_all_asset, true, currency, true) }
            </div>
            <div styleName='ticker-assets-label'>
              &nbsp;invested
            </div>
          </div>
          <div styleName='ticker-assets'>
            <div styleName='ticker-assets-figure'>
              {format.perc(po_all_weight) }
            </div>
            <div styleName='ticker-assets-label'>
              &nbsp;of portfolio
            </div>
          </div>
        </div>
        <div styleName={'score-wrapper'}>
          <Grade grade={grade_deforestation} />
        </div>
      </div>
    )
  }
}


/**
 * <FamilyFundListItemTickerLinks 
 *   shareclasses
 * />
 * 
 * Displays a list of tickers, linked to those shareclass profiles, for a fund
 * in the fund list. Maps a list of shareclasses to a list of URLs and
 * tickers. Maps the list of URLs and tickers to a list of Links.
 * 
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyFundListItemTickerLinks extends Component {
  constructor(props) {
    super(props)
    this.state = {
      shareclasses: props.shareclasses || null
    }
  }

  render() {
    const { shareclasses } = this.state
    if (!shareclasses) { return null }

    const tickerUrls = shareclasses.map((e,i)=>{
      const tckr = e.ticker || 'N/A'
      const safeTicker = e.ticker || 'N'
      const url = config.routes.fund + '/' +
        format.sanitize(e.fund_legal_name) + '/' + safeTicker + '/' +
        config.profilePath + '/' + e.fund_id + '/' + e.investment_vehicle_id

      return {
        ticker: tckr,
        name: e.name,
        url
      }
    })

    if (tickerUrls.length === 1 && tickerUrls[0].ticker === 'N/A') {
      const e = tickerUrls[0]
      return (
        <div styleName='ticker-links'>
            <Link to={e.url} title={e.name}>
              <span styleName='ticker'>See results<LinkIcon/></span>
            </Link>
        </div>
      )
    }

    return (
      <div styleName='ticker-links'>
        {
          tickerUrls.map((e, i)=>{
            return (
              <Link
                key={'holding-link-'+i}
                to={e.url}
                title={e.name}
              >
                { e.ticker === 'N/A' ?
                    <span styleName='ticker'>{e.name}</span> :
                    <span styleName='ticker'>{e.ticker}</span>
                }
              </Link>
            )
          })
        }
      </div>
    )
  }
}


@connect(
  state => ({
    result: state.familyShareClass.result,
    searching: state.familyShareClass.loading,
    searched: state.familyShareClass.loaded,
    error: state.familyShareClass.error
  }),
  searchActions)
@CSSModules(Styles, {allowMultiple:true})
export default class Family extends Component {
  static propTypes = {
    searching: PropTypes.bool,
    searched: PropTypes.bool,
    error: PropTypes.object,
    result: PropTypes.array,
  }

  constructor(props) {
    super(props)
    this.state = {
      page: 1,
      family: props.location.query.q || '',
      searchQuery: '',
      lastResult: null,
      funds: [],
    }
  }

  componentWillMount() {
    if (typeof window !== 'undefined') {
      window.scrollTo(0,0)
    }
    this.familyShareClasses(this.state.family, 1)
  }

  componentWillReceiveProps(props) {
    const q = props.location.query.q || ''
    if (q !== this.state.family) {
      this.familyShareClasses(q, 1)
      window.scroll(0, 0)
      this.setState({
        page: 1,
        family: q,
        searchQuery: '',
        lastResult: null,
        funds: [],
      })
    } else if (props.result && this.state.lastResult !== props.result){
      this.setState({
        lastResult: props.result,
        funds: this.state.funds.concat(props.result)
      })
    }
  }

  getMoreFunds = () => {
    this.familyShareClasses(this.state.family, this.state.page + 1)
  }

  familyShareClasses = (family, page) => {
    page = page || this.state.page
    this.setState({page:page})
    this.props.familyShareClasses(family, page)
  }

  configFundFamily = () => {
    const ff = config.familyFunds
    return ff.find((family) => family.fund_family_name === this.state.family)
  }

  configEngagementFamily = () => {
    const ef = config.engagementFunds
    return ef.find((family) => family.name === this.state.family)
  }

  renderEngagement = () => {
    const engagementFamily = this.configEngagementFamily()
    if( !engagementFamily ){
      return null
    }

    return (
      <div styleName='engagement-description'>
        <p>
          Some sustainability-mandated funds actively engage the companies
          they own. Indeed, efforts by sustainability-focused investors whose
          funds are found on this site have been crucial to driving positive
          change in deforestation-risk sectors like palm oil. A number of
          asset managers are <strong>known sustainability engagers</strong>,
          and have taken actions such as filing
          &#160;
          <Link
            xstrong
            to={config.links.aysvote}
            title='Vote Your Shares | As You Sow'
          >
            shareholder resolutions
          </Link>
          &#160;
          asking for policy changes. Those asset managers deserve special
          acknowledgement - often, they’re using their investments to make
          sure they have a voice at the table to call for real improvements in
          company behavior. We used
          &#160;
          <Link
            xstrong
            to={config.links.ceres}
            title='Ceres Shareholder Resolutions Database'
          >
            data collected by Ceres
          </Link>
          &#160;
          to identify asset managers that have filed shareholder resolutions
          on deforestation, climate change, or similar environmental issues.
          If a fund has investments in companies we screen for, but the asset
          manager is a known filer of deforestation or environmental-related
          shareholder resolutions, we assign a special engagement score.
        </p>
        { /* if there is a list of companies filed at */
          engagementFamily.filedAt ? (
            <p>
              {engagementFamily.name} has filed deforestation or other
              climate-change related shareholder resolutions at&#160;
              <strong>{engagementFamily.filedAt}</strong>
              &#160;
              <Link
                xstrong
                to={config.links.ceres}
                title='Ceres Shareholder Resolutions Database'
              >
                See the resolutions<LinkIcon />
              </Link>
            </p>
          ) : null
        }
        <p>
          <Link
            xstrong={true}
            to={config.routes.faq + '#7'}
            title={'Where does the “Deforestation Grade" come from that I ' +
                   'see when I search a fund? | FAQ | Deforestation Free  ' +
                   'Funds'}
          >
            Read more about our deforestation grades.
          </Link>
        </p>
        { /* if there is an engagment family statement */
          engagementFamily.statement ? (
            <div styleName='engagement-statement'>
              <div styleName='engagement-statement-title'>
                {engagementFamily.name} submitted a statement describing their
                engagement efforts:
              </div>
              <blockquote styleName='engagement-statement-quote'>
                {engagementFamily.statement}
              </blockquote>
            </div>
          ) : null
        }
      </div>
    )
  }

  renderBox = () => {
    const fundFamily = this.configFundFamily()
    if( !fundFamily ){
      return null
    }
    const name = fundFamily.fund_family_name
    const familiesWithPetitions = Object.keys(config.fundFamilyPetitions.data)
    const hasPetition = familiesWithPetitions.indexOf(name) >= 0
    const entry = hasPetition ? config.fundFamilyPetitions.data[name] : null
    const petitionLink = entry ? entry[0] : null
    const petitionText = entry ? entry[1] : null
    const defaultCopy = 'Major fund managers invest millions into ' +
      'agricultural commodity producers and traders with business models ' +
      'that destroy the rainforest, and billions more into the banks and ' +
      'lenders that finance deforestation-risk producers, and the consumer ' +
      'brands that use these unsustainably-produced commodities in their ' +
      'products. Click below to learn more about the impact of ' +
      'agricultural commodity production on global rainforests.'
    const buttonText = petitionLink ? 'Sign the petition' : 'Learn more'

    return (
      <div styleName='company-snapshot'>
        <div styleName='company-intro'>
          {petitionText || defaultCopy}
        </div>
        <Link
          title={
            'Sign the petition to ask ' +
            name +
            'for a No Deforestation Policy'
          }
          log={analytics.eventToFamilyTakeAction(name)}
          to={petitionLink || config.routes.faq}
          targetBlank
        >
          <div styleName='company-wrapper'>
            <span styleName='company'>{buttonText}<LinkIcon /></span>
          </div>
        </Link>
      </div>
    )
  }

  renderFamily = () => {
    function getPerc(asset,totalAsset){
      asset = convert.int(asset)
      totalAsset = convert.int(totalAsset)
      if( totalAsset === 0 ){
        return 0
      }
      // return Math.round( asset/ totalAsset * 100 ) / 100
      return parseFloat((asset / totalAsset * 100).toFixed(2))
    }

    // is this a fund family 
    const fundFamily = this.configFundFamily()
    if (!fundFamily) {
      return null
    }

    /*  We currently have five asset managers with factsheets */
    const familiesWithFactSheets = ['Vanguard', 'BlackRock/iShares',
      'Fidelity Investments', 'SPDR State Street Global Advisors',
      'TIAA Investments/Nuveen']
    const name = fundFamily.fund_family_name
    const hasFactSheet = familiesWithFactSheets.indexOf(name) >= 0

    const efna = fundFamily.equity_fund_net_assets
    const producerPerc = getPerc(fundFamily.df_producer_asset_sum, efna)
    const bankPerc =  getPerc(fundFamily.df_financier_asset_sum, efna)
    const consumerPerc =  getPerc(fundFamily.df_consumer_asset_sum, efna)
    const dffPerc =  getPerc(fundFamily.df_all_asset_sum, efna)

    return (
      <div>
        <Chart
          renderBox={CSSModules(this.renderBox, Styles)}
          currency={'US Dollar'}
          invested={convert.int(fundFamily.df_all_asset_sum)}
          bankPerc={bankPerc}
          consumerPerc={consumerPerc}
          producerPerc={producerPerc}
          dffPerc={dffPerc}
          famType
        />
        <FlaggedFund
          isFamily
          totalFunds={convert.int(fundFamily.num_equity_funds)}
          count={convert.int(fundFamily.df_all_count_sum)}
          weight={convert.float(fundFamily.df_all_weight_total)}
        />

        <div styleName='chart-content-hl-wrap'>
          <p>
            <LinkIcon faclass='fa-info-circle' nospace />&nbsp;
            This data covers open-end mutual funds and exchange-traded funds
            domiciled in the
            U.S. with at least 50% of assets invested in stocks. It
            excludes bond funds, target date funds, and other investment vehicles
            like closed-end funds, collective trusts, and funds domiciled
            outside the U.S. <b>This asset manager may have additional
            deforestation-risk investments in those excluded funds.</b>
          </p>
        </div>

        {hasFactSheet ? <FamilyFactSheet fam={fundFamily} /> : null}
      </div>
    )
  }

  render() {
    if (!this.props.location.query.q) {
      return (
        <div styleName='main-grid'>
          <FamilyGrid fullGrid />
        </div>
      )
    }
    if (!(this.state.page !== 1 || !this.props.searching)) {
      return (
        <div styleName='main'>
          <Loader options={{scale: 0.70, top:'2.0rem'}} loaded={false} />
        </div>
      )
    }


    // redirect for SEO
    if (
      this.props.location.query.q.toUpperCase()==='BLACKROCK' ||
      this.props.location.query.q.toUpperCase()==='ISHARES'
    ) {
      this.context.router.push(
        config.routes.families + '?q=BlackRock%2FiShares'
      )
    }

    const { funds } = this.state

    const fundFamily = this.configFundFamily()

    const fileName = format.imgPath(this.state.family)
    let hasLogo = false
    let companyLogo = null
    try {
      companyLogo = require('../../../static/images/fam/'+fileName+'.png')
      hasLogo = true
    }
    catch (e) {
      console.log('')
    }

    const hasMore = (
      this.state.lastResult &&
      this.state.lastResult.length === config.queryMaxFunds
    )

    return (
      <div styleName='main'>
        {container.helmet(this.state.family)}
        <Loader
          options={{scale: 0.70, top: '2.0rem'}}
          loaded={ this.state.page !== 1 || !this.props.searching}
        >
          <div styleName='name-wrap-background'>
            <ContainerDivs>
              <div styleName='name-wrap'>
                <div styleName='name'>
                  <div>
                    <div styleName={fundFamily ? 'name-tag-big' : 'name-tag'}>
                      {fundFamily ? 'Major fund manager' : 'Fund manager'}
                    </div>
                     <div styleName='name-logo-lbl'>
                      {
                        fundFamily ?
                          fundFamily.label :
                          this.state.family
                      }
                    </div>
                  </div>
                </div>
              </div>
            </ContainerDivs>
          </div>

          <ContainerDivs>

            { /*
            {this.renderEngagement()}
            */ }
            
            { /*
            {this.renderFamily()}
            */ }

            { /*
            <br/>
            <br/>
            */ }

            <FamilySummarySentence fundFamily={fundFamily}/>
            <div styleName='holdings-inner'>
              <div styleName='rating-label'>
                <div styleName='rating-label-line' />
                <div styleName='rating-label-info'>Deforestation grade</div>
              </div>
              <FamilyFundList fundList={funds} />
            </div>
          </ContainerDivs>

          <div styleName='more-funds-wrapper'>
            <Loader
              options={{scale: 0.70, top:'0'}}
              loaded={ !this.props.searching }
            />
            <div styleName='more-funds'>
              <div
                onClick={hasMore ? this.getMoreFunds : null}
                styleName={
                  hasMore ? 'more-funds-button' : 'no-more-funds-button'
                }
              >
                {
                  hasMore ?
                    'Get more funds' : 
                    'No more funds'
                }<LinkIcon faclass='fa-plus-circle'/>
              </div>
              {
                !hasMore ?
                  (
                    <div styleName='no-more-funds-info'>
                      Looking for your favorite fund and can't find it? Sorry,
                      but we don't have everything in our database.
                    </div>
                  ) :
                  null
              }
            </div>
          </div>

          <FamilyBanner fundFamily={fundFamily}/>

          <ContainerDivs>
            <AboutBanner />
          </ContainerDivs>

        </Loader>
      </div>
    )
  }
}
